import { useAuth0 } from "@auth0/auth0-vue"
import { computed, ref } from "vue";
import { selectedAccount } from "../store/state.js";
import {
    getAccounts,
    getRoles,
    getRoleAssignments,
    getUsers,
    getUserGroups,
    getLlmQuotaAssignments,
    getModelDeploymentCredentials,
    getModelDeployments,
    getLlmQuotas,
    getKnowledgeSources,
    getModels,
    getUserGroupMemberships,
    getKnowledgeSourceTypes,
    getSkills,
    getSkillAccessGrants,
    getSkillSteps,
    getSkillStepTypes,
    getLlmModelDeploymentAccess,
    getSkillGroupings,
    getSkillGroupingAssignments,
    getAiBots,
    getAiBotSkillAssignments,
    getAiBotAccessGrants
} from "../util/api.js";
import {
    decodeAccount,
    decodeKnowledgeSource,
    decodeKnowledgeSourceType,
    decodeLlmQuota,
    decodeLlmQuotaAssignment,
    decodeLlmModelDeploymentAccess,
    decodeModel,
    decodeModelDeployment,
    decodeModelDeploymentCredential, decodeSkill, decodeSkillAccessGrant, decodeSkillStep, decodeSkillStepType,
    decodeUser,
    decodeUserGroup,
    decodeUserGroupMembership,
    decodeSkillGrouping,
    decodeSkillGroupingAssignment,
    decodeAiBot,
    decodeAiBotSkillAssignment,
    decodeAiBotAccessGrant
} from "../util/util.js"

const usersLoading = ref(false)
const users = ref([])
const roles = ref({})

export async function loadUsers() {
    usersLoading.value = true

    roles.value = {}
    const roleItems = await getRoles(selectedAccount.value)
    roles.value = {}
    for (let i = 0; i < roleItems?.length; i++) {
        roles.value[roleItems[i].id] = {
            name: roleItems[i].title,
            id: roleItems[i].id
        }
    }

    const roleAssignments = {}
    const roleAssignmentItems = await getRoleAssignments(selectedAccount.value)
    for (let i = 0; i < roleAssignmentItems?.length; i++) {
        roleAssignments[roleAssignmentItems[i].cephisso_platform_users_id] = roleAssignments[roleAssignmentItems[i].cephisso_platform_users_id] || []
        roleAssignments[roleAssignmentItems[i].cephisso_platform_users_id].push({
            name: roles.value[roleAssignmentItems[i].cephisso_roles_id].name,
            roleId: roleAssignmentItems[i].cephisso_roles_id,
            assignmentId: roleAssignmentItems[i].id
        })
    }

    users.value = []
    const items = await getUsers(selectedAccount.value)
    users.value = []
    for (let i = 0; i < items?.length; i++) {
        users.value.push(decodeUser(items[i], roleAssignments[items[i].id]))
    }

    usersLoading.value = false
}

const userGroupsLoading = ref(false)
const userGroups = ref([])
const userGroupMemberships = ref([])

export async function loadUserGroups() {
    userGroupsLoading.value = true

    userGroupMemberships.value = []
    const groupMembershipItems = await getUserGroupMemberships(selectedAccount.value)
    userGroupMemberships.value = []
    for (let i = 0; i < groupMembershipItems?.length; i++) {
        userGroupMemberships.value.push(decodeUserGroupMembership(groupMembershipItems[i]))
    }

    userGroups.value = []
    const items = await getUserGroups(selectedAccount.value)
    userGroups.value = []
    for (let i = 0; i < items?.length; i++) {
        userGroups.value.push(decodeUserGroup(items[i]))
    }

    userGroupsLoading.value = false
}

export function useUsers() {

    const { user } = useAuth0();

    const myUserRoles = computed(() => {
        const roles = []
        if (!selectedAccount.value || !user.value?.sub || usersLoading.value) {
            return roles
        }

        for (let i = 0; i < users.value?.length; i++) {
            if (users.value[i].auth0Id === user.value.sub) {
                for (let j = 0; j < users.value[i]?.roles?.length; j++) {
                    roles.push(users.value[i].roles[j].name)
                }
            }
        }
        return roles
    })

    const canManageUsers = computed(() => {
        return myUserRoles.value.indexOf('Administrator') > -1 || myUserRoles.value.indexOf('Admin: No Data Access') > -1 || myUserRoles.value.indexOf('Manage Users') > -1
    })

    const hasWriteAccess = computed(() => {
        return myUserRoles.value.indexOf('Administrator') > -1 || myUserRoles.value.indexOf('Admin: No Data Access') > -1 || myUserRoles.value.indexOf('Read/Write') > -1
    })
    const hasDeleteAccess = computed(() => {
        return myUserRoles.value.indexOf('Administrator') > -1 || myUserRoles.value.indexOf('Admin: No Data Access') > -1 || myUserRoles.value.indexOf('Delete') > -1
    })

    const hasAdminAccess = computed(() => {
        return myUserRoles.value.indexOf('Administrator') > -1 || myUserRoles.value.indexOf('Admin: No Data Access') > -1
    })

    return { users, roles, loadUsers, usersLoading, userGroups, userGroupMemberships, loadUserGroups, userGroupsLoading, canManageUsers, hasWriteAccess, hasDeleteAccess, hasAdminAccess }
}

const accountsLoading = ref(false)
const accounts = ref([])
const createAccountDialogVisible = ref(false)
const selectAccountDialogVisible = ref(false)

function showAccountSelectDialog() {
    createAccountDialogVisible.value = false
    selectAccountDialogVisible.value = true
}
function showCreateAccountDialog() {
    createAccountDialogVisible.value = true
    selectAccountDialogVisible.value = false
}

function showAccountDialog() {
    if (accountsLoading.value) {
        return
    }
    if (accounts.value?.length >= 1) {
        showAccountSelectDialog()
    }
    else {
        showCreateAccountDialog()
    }
}

export async function loadAccounts() {
    accountsLoading.value = true
    const items = await getAccounts()
    accounts.value = []
    for (let i = 0; i < items?.length; i++) {
        if (items[i].id > 0) {
            accounts.value.push(decodeAccount(items[i]))
        }
    }
    accountsLoading.value = false
}

export function useAccounts() {

    function getAccount(id) {
        for (let i = 0; i < accounts.value?.length; i++) {
            if (String(accounts.value[i]?.id) === String(id)) {
                return accounts.value[i]
            }
        }
        return null
    }

    const selectedAccountDetails = computed(() => {
        if (selectedAccount.value === null || accountsLoading.value) {
            return null
        }

        return getAccount(selectedAccount.value)
    })

    const selectedAccountName = computed(() => {
        if (selectedAccount.value === null || accountsLoading.value) {
            return null
        }

        const a = selectedAccountDetails.value

        return a ? a.name : '<Error>'
    })

    const nestedAccounts = computed(() => {
        const hierarchy = {}
        const accountsById = {}
        const a = accounts.value
        if (!a?.length) {
            return []
        }
        for (const item of a) {
            accountsById[item.id] = {
                name: item.name,
                id: item.id,
                parentId: item.parentId,
                children: [],
            }
        }

        a.forEach(item => {
            const parentId = item.parentId;
            if (parentId) {
                const parent = accountsById[parentId];
                if (parent) {
                    parent.children.push(accountsById[item.id]);
                    parent.children.sort((a, b) => a.name.localeCompare(b.name))
                }
                else {
                    hierarchy[item.id] = accountsById[item.id];
                }
            }
            else {
                hierarchy[item.id] = accountsById[item.id];
            }
        });

        const h = Object.values(hierarchy).sort((a, b) => a.name.localeCompare(b.name))

        const getAccountsRecursive = (a, depth) => {
            let o = []
            for (let i = 0; i < a?.length; i++) {
                o.push({ name: '—'.repeat(depth) + (depth > 0 ? ' ' : '') + a[i].name, displayName: a[i].name, id: a[i].id })
                if (a[i].children?.length) {
                    o = o.concat(getAccountsRecursive(a[i].children, depth + 1))
                }
            }
            return o
        }

        return getAccountsRecursive(h, 0)

    })

    return { accounts, selectedAccountName, accountsLoading, loadAccounts,
        createAccountDialogVisible, selectAccountDialogVisible,
        showCreateAccountDialog, showAccountDialog, selectedAccountDetails, nestedAccounts }
}

const knowledgeSourcesLoading = ref(false)
const knowledgeSources = ref([])
const knowledgeSourceTypes = ref([])

export async function loadKnowledgeSources() {
    knowledgeSourcesLoading.value = true

    knowledgeSourceTypes.value = []
    const typeItems = await getKnowledgeSourceTypes(selectedAccount.value)
    knowledgeSourceTypes.value = []
    for (let i = 0; i < typeItems?.length; i++) {
        knowledgeSourceTypes.value.push(decodeKnowledgeSourceType(typeItems[i]))
    }
    knowledgeSourceTypes.value.sort((a, b) => a.name?.localeCompare(b?.name))

    knowledgeSources.value = []
    const items = await getKnowledgeSources(selectedAccount.value)
    knowledgeSources.value = []
    for (let i = 0; i < items?.length; i++) {
        knowledgeSources.value.push(decodeKnowledgeSource(items[i]))
    }

    knowledgeSourcesLoading.value = false
}

export function useKnowledgeSources() {
    return { knowledgeSources, knowledgeSourceTypes, knowledgeSourcesLoading, loadKnowledgeSources }
}

const modelDeploymentsLoading = ref(false)
const models = ref([])
const modelDeployments = ref([])
const modelDeploymentCredentials = ref([])

export async function loadModelDeployments() {
    modelDeploymentsLoading.value = true

    modelDeploymentCredentials.value = []
    const credentialItems = await getModelDeploymentCredentials(selectedAccount.value)
    modelDeploymentCredentials.value = []
    for (let i = 0; i < credentialItems?.length; i++) {
        modelDeploymentCredentials.value.push(decodeModelDeploymentCredential(credentialItems[i]))
    }

    models.value = []
    const modelIems = await getModels(selectedAccount.value)
    models.value = []
    for (let i = 0; i < modelIems?.length; i++) {
        models.value.push(decodeModel(modelIems[i]))
    }

    modelDeployments.value = []
    const items = await getModelDeployments(selectedAccount.value)
    modelDeployments.value = []
    for (let i = 0; i < items?.length; i++) {
        modelDeployments.value.push(decodeModelDeployment(items[i], models))
    }

    modelDeploymentsLoading.value = false
}

export function useModelDeployments() {
    const deploymentCredentials = computed(() => {
        return modelDeploymentCredentials.value.filter(cred => 
            !cred.metadata?.keyType || cred.metadata.keyType === 'llm-model'
        )
    })

    const searchCredentials = computed(() => {
        return modelDeploymentCredentials.value.filter(cred => 
            cred.metadata?.keyType === 'search'
        )
    })

    return { 
        models, 
        modelDeployments, 
        modelDeploymentCredentials, 
        deploymentCredentials,
        searchCredentials,
        modelDeploymentsLoading, 
        loadModelDeployments 
    }
}

const llmQuotasLoading = ref(false)
const llmQuotas = ref([])
const llmQuotaAssignments = ref([])

export async function loadLlmQuotas() {
    llmQuotasLoading.value = true

    llmQuotaAssignments.value = []
    const assignmentItems = await getLlmQuotaAssignments(selectedAccount.value)
    llmQuotaAssignments.value = []
    for (let i = 0; i < assignmentItems?.length; i++) {
        llmQuotaAssignments.value.push(decodeLlmQuotaAssignment(assignmentItems[i]))
    }

    llmQuotas.value = []
    const items = await getLlmQuotas(selectedAccount.value)
    llmQuotas.value = []
    for (let i = 0; i < items?.length; i++) {
        llmQuotas.value.push(decodeLlmQuota(items[i]))
    }

    llmQuotasLoading.value = false
}

export function useLlmQuotas() {
    return { llmQuotas, llmQuotaAssignments, llmQuotasLoading, loadLlmQuotas }
}


const skillsLoading = ref(false)
const skills = ref([])
const skillStepTypes = ref([])
const skillSteps = ref([])
const skillAccessGrants = ref([])

export async function loadSkills() {
    skillsLoading.value = true

    skillAccessGrants.value = []
    const assignmentItems = await getSkillAccessGrants(selectedAccount.value)
    skillAccessGrants.value = []
    for (let i = 0; i < assignmentItems?.length; i++) {
        skillAccessGrants.value.push(decodeSkillAccessGrant(assignmentItems[i]))
    }

    skillStepTypes.value = []
    const stepTypeItems = await getSkillStepTypes(selectedAccount.value)
    skillStepTypes.value = []
    for (let i = 0; i < stepTypeItems?.length; i++) {
        skillStepTypes.value.push(decodeSkillStepType(stepTypeItems[i]))
    }
    skillStepTypes.value.sort((a, b) => a.name?.localeCompare(b?.name))

    skillSteps.value = []
    const stepItems = await getSkillSteps(selectedAccount.value)
    skillSteps.value = []
    for (let i = 0; i < stepItems?.length; i++) {
        skillSteps.value.push(decodeSkillStep(stepItems[i]))
    }

    skills.value = []
    const items = await getSkills(selectedAccount.value)
    skills.value = []
    for (let i = 0; i < items?.length; i++) {
        skills.value.push(decodeSkill(items[i]))
    }

    skillsLoading.value = false
}

export function useSkills() {
    return { skills, skillStepTypes, skillSteps, skillAccessGrants, skillsLoading, loadSkills }
}

const llmModelDeploymentAccessLoading = ref(false)
const llmModelDeploymentAccess = ref([])

export async function loadLlmModelDeploymentAccess() {
    llmModelDeploymentAccessLoading.value = true

    llmModelDeploymentAccess.value = []
    const items = await getLlmModelDeploymentAccess(selectedAccount.value)
    llmModelDeploymentAccess.value = []
    for (let i = 0; i < items?.length; i++) {
        llmModelDeploymentAccess.value.push(decodeLlmModelDeploymentAccess(items[i]))
    }

    llmModelDeploymentAccessLoading.value = false
}

export function useLlmModelDeploymentAccess() {
    return { llmModelDeploymentAccess, llmModelDeploymentAccessLoading, loadLlmModelDeploymentAccess }
}

const skillGroupingsLoading = ref(false)
const skillGroupings = ref([])
const skillGroupingAssignments = ref([])

export async function loadSkillGroupings() {
    skillGroupingsLoading.value = true

    skillGroupingAssignments.value = []
    const assignmentItems = await getSkillGroupingAssignments(selectedAccount.value)
    skillGroupingAssignments.value = []
    for (let i = 0; i < assignmentItems?.length; i++) {
        skillGroupingAssignments.value.push(decodeSkillGroupingAssignment(assignmentItems[i]))
    }

    skillGroupings.value = []
    const items = await getSkillGroupings(selectedAccount.value)
    skillGroupings.value = []
    for (let i = 0; i < items?.length; i++) {
        skillGroupings.value.push(decodeSkillGrouping(items[i]))
    }

    skillGroupingsLoading.value = false
}

export function useSkillGroupings() {
    return { skillGroupings, skillGroupingAssignments, skillGroupingsLoading, loadSkillGroupings }
}

const aiBotsLoading = ref(false)
const aiBots = ref([])
const aiBotSkillAssignments = ref([])
const aiBotAccessGrants = ref([])

export async function loadAiBots() {
    aiBotsLoading.value = true

    aiBotAccessGrants.value = []
    const grantItems = await getAiBotAccessGrants(selectedAccount.value)
    aiBotAccessGrants.value = []
    for (let i = 0; i < grantItems?.length; i++) {
        aiBotAccessGrants.value.push(decodeAiBotAccessGrant(grantItems[i]))
    }

    aiBotSkillAssignments.value = []
    const assignmentItems = await getAiBotSkillAssignments(selectedAccount.value)
    aiBotSkillAssignments.value = []
    for (let i = 0; i < assignmentItems?.length; i++) {
        aiBotSkillAssignments.value.push(decodeAiBotSkillAssignment(assignmentItems[i]))
    }

    aiBots.value = []
    const items = await getAiBots(selectedAccount.value)
    aiBots.value = []
    for (let i = 0; i < items?.length; i++) {
        aiBots.value.push(decodeAiBot(items[i]))
    }

    aiBotsLoading.value = false
}

export function useAiBots() {
    return { aiBots, aiBotSkillAssignments, aiBotAccessGrants, aiBotsLoading, loadAiBots }
}